import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Mail,
  Search,
  Eye,
  CheckCircle,
  XCircle,
  Loader2,
  Clock,
  User,
  Phone,
  Calendar,
  MessageSquare,
  Building2,
} from "lucide-react";
import { useAuth } from "@/components/auth-provider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { AppDialog } from "@/components/ui/app-dialog";
import { InquiryStatusBadge } from "@/lib/inquiry-utils";
import {
  subscribeToLandlordInquiries,
  getInquiryStats,
  replyToInquiry,
  updateInquiryStatus,
} from "@/lib/inquiry-service";
import { PropertyInquiry, InquiryStatus } from "@/types/types";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

// Zod schema for reply form
const replyFormSchema = z.object({
  reply: z
    .string()
    .min(10, "Reply must be at least 10 characters")
    .max(1000, "Reply must be less than 1000 characters"),
});

type ReplyFormData = z.infer<typeof replyFormSchema>;

export default function InquiriesPage() {
  const { user } = useAuth();

  // State for data
  const [inquiries, setInquiries] = useState<PropertyInquiry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<{
    total: number;
    new: number;
    replied: number;
    closed: number;
  } | null>(null);

  // State for filters and search
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<InquiryStatus | "all">("all");

  // State for dialogs
  const [selectedInquiry, setSelectedInquiry] = useState<PropertyInquiry | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isReplyDialogOpen, setIsReplyDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form instance
  const replyForm = useForm<ReplyFormData>({
    resolver: zodResolver(replyFormSchema),
    defaultValues: {
      reply: "",
    },
  });

  // Load inquiries and stats
  useEffect(() => {
    if (!user?.id) return;

    setIsLoading(true);

    // Subscribe to real-time inquiry updates
    const unsubscribe = subscribeToLandlordInquiries(
      user.id,
      (data) => {
        setInquiries(data);
        setIsLoading(false);
      },
      (error) => {
        console.error("Error subscribing to inquiries:", error);
        toast.error("Failed to load inquiries");
        setIsLoading(false);
      },
      statusFilter !== "all" ? statusFilter : undefined
    );

    // Load stats
    getInquiryStats(user.id)
      .then((statsData) => setStats(statsData))
      .catch((error) => {
        console.error("Error loading inquiry stats:", error);
      });

    return () => unsubscribe?.();
  }, [user?.id, statusFilter]);

  // Filter inquiries by search term
  const filteredInquiries = inquiries.filter((inquiry) => {
    if (!searchTerm) return true;
    
    const term = searchTerm.toLowerCase();
    return (
      inquiry.propertyTitle.toLowerCase().includes(term) ||
      inquiry.inquirerName.toLowerCase().includes(term) ||
      inquiry.inquirerEmail.toLowerCase().includes(term) ||
      inquiry.message.toLowerCase().includes(term)
    );
  });

  // Handle view inquiry details
  const handleViewInquiry = (inquiry: PropertyInquiry) => {
    setSelectedInquiry(inquiry);
    setIsViewDialogOpen(true);
  };

  // Handle open reply dialog
  const handleOpenReplyDialog = (inquiry: PropertyInquiry) => {
    setSelectedInquiry(inquiry);
    replyForm.reset({
      reply: inquiry.landlordReply || "",
    });
    setIsReplyDialogOpen(true);
  };

  // Handle submit reply
  const handleSubmitReply = async (data: ReplyFormData) => {
    if (!selectedInquiry) return;

    setIsSubmitting(true);
    try {
      await replyToInquiry(selectedInquiry.id, data.reply);
      toast.success("Reply sent successfully");
      setIsReplyDialogOpen(false);
      replyForm.reset();
    } catch (error) {
      console.error("Error sending reply:", error);
      toast.error("Failed to send reply");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle update status
  const handleUpdateStatus = async (inquiryId: string, status: InquiryStatus) => {
    try {
      await updateInquiryStatus(inquiryId, status);
      toast.success(`Inquiry marked as ${status}`);
    } catch (error) {
      console.error("Error updating inquiry status:", error);
      toast.error("Failed to update status");
    }
  };

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get days since inquiry
  const getDaysSince = (dateStr: string) => {
    const today = new Date();
    const inquiryDate = new Date(dateStr);
    const diffTime = today.getTime() - inquiryDate.getTime();
    const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return days;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Inquiries</CardTitle>
            <Mail className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.total || 0}</div>
            <p className="text-xs text-muted-foreground">
              All time property inquiries
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">New Inquiries</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.new || 0}</div>
            <p className="text-xs text-muted-foreground">
              Awaiting your response
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Replied</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.replied || 0}</div>
            <p className="text-xs text-muted-foreground">
              Response sent
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Closed</CardTitle>
            <XCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.closed || 0}</div>
            <p className="text-xs text-muted-foreground">
              Completed inquiries
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <div className="relative w-full sm:w-[300px]">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search inquiries..."
              className="pl-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <Select
            value={statusFilter}
            onValueChange={(value) => setStatusFilter(value as InquiryStatus | "all")}
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="replied">Replied</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Inquiries Table */}
      <Card>
        <CardHeader>
          <CardTitle>Property Inquiries</CardTitle>
          <CardDescription>
            Manage inquiries from potential tenants. Showing {filteredInquiries.length} of{" "}
            {inquiries.length} inquiries.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Property</TableHead>
                <TableHead>Inquirer</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredInquiries.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center py-8 text-muted-foreground"
                  >
                    {searchTerm
                      ? "No inquiries found matching your search."
                      : "No inquiries yet. They will appear here when potential tenants contact you."}
                  </TableCell>
                </TableRow>
              ) : (
                filteredInquiries.map((inquiry) => (
                  <TableRow key={inquiry.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{inquiry.propertyTitle}</div>
                          {inquiry.moveInDate && (
                            <div className="text-xs text-muted-foreground">
                              Move-in: {new Date(inquiry.moveInDate).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">{inquiry.inquirerName}</span>
                        <span className="text-xs text-muted-foreground">
                          {inquiry.inquirerEmail}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span>{formatDate(inquiry.createdAt)}</span>
                        <span className="text-xs text-muted-foreground">
                          {getDaysSince(inquiry.createdAt)} days ago
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <InquiryStatusBadge status={inquiry.status} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewInquiry(inquiry)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                        {inquiry.status !== "closed" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenReplyDialog(inquiry)}
                          >
                            <MessageSquare className="h-4 w-4 mr-1" />
                            {inquiry.status === "new" ? "Reply" : "Edit Reply"}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* View Inquiry Details Dialog */}
      <AppDialog
        open={isViewDialogOpen}
        onOpenChange={setIsViewDialogOpen}
        title="Inquiry Details"
        description="Complete information about this property inquiry"
        icon={<Mail className="h-5 w-5" />}
        maxWidth="2xl"
      >
        {selectedInquiry && (
          <div className="space-y-6">
            {/* Property Info */}
            <div className="bg-muted/50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="h-5 w-5 text-muted-foreground" />
                <h3 className="font-semibold text-lg">{selectedInquiry.propertyTitle}</h3>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <InquiryStatusBadge status={selectedInquiry.status} />
                <span>•</span>
                <span>{formatDate(selectedInquiry.createdAt)}</span>
              </div>
            </div>

            {/* Inquirer Info */}
            <div className="space-y-3">
              <h4 className="font-semibold text-base">Contact Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <User className="h-4 w-4 text-muted-foreground mt-1" />
                  <div>
                    <p className="text-sm text-muted-foreground">Name</p>
                    <p className="font-medium">{selectedInquiry.inquirerName}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="h-4 w-4 text-muted-foreground mt-1" />
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium">{selectedInquiry.inquirerEmail}</p>
                  </div>
                </div>
                {selectedInquiry.inquirerPhone && (
                  <div className="flex items-start gap-3">
                    <Phone className="h-4 w-4 text-muted-foreground mt-1" />
                    <div>
                      <p className="text-sm text-muted-foreground">Phone</p>
                      <p className="font-medium">{selectedInquiry.inquirerPhone}</p>
                    </div>
                  </div>
                )}
                {selectedInquiry.moveInDate && (
                  <div className="flex items-start gap-3">
                    <Calendar className="h-4 w-4 text-muted-foreground mt-1" />
                    <div>
                      <p className="text-sm text-muted-foreground">Desired Move-in</p>
                      <p className="font-medium">
                        {new Date(selectedInquiry.moveInDate).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Message */}
            <div className="space-y-2">
              <h4 className="font-semibold text-base">Message</h4>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm whitespace-pre-wrap">{selectedInquiry.message}</p>
              </div>
            </div>

            {/* Reply (if exists) */}
            {selectedInquiry.landlordReply && (
              <div className="space-y-2">
                <h4 className="font-semibold text-base">Your Reply</h4>
                <div className="bg-blue-50 dark:bg-blue-950/20 p-4 rounded-lg border border-blue-200 dark:border-blue-900">
                  <p className="text-sm whitespace-pre-wrap">{selectedInquiry.landlordReply}</p>
                  {selectedInquiry.repliedAt && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Sent on {formatDate(selectedInquiry.repliedAt)}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              {selectedInquiry.status !== "closed" && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsViewDialogOpen(false);
                      handleOpenReplyDialog(selectedInquiry);
                    }}
                  >
                    <MessageSquare className="mr-2 h-4 w-4" />
                    {selectedInquiry.status === "new" ? "Reply" : "Edit Reply"}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      handleUpdateStatus(selectedInquiry.id, "closed");
                      setIsViewDialogOpen(false);
                    }}
                  >
                    <XCircle className="mr-2 h-4 w-4" />
                    Mark as Closed
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </AppDialog>

      {/* Reply Dialog */}
      <AppDialog
        open={isReplyDialogOpen}
        onOpenChange={setIsReplyDialogOpen}
        title="Reply to Inquiry"
        description="Send a response to this potential tenant"
        icon={<MessageSquare className="h-5 w-5" />}
        maxWidth="2xl"
        footer={
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setIsReplyDialogOpen(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={replyForm.handleSubmit(handleSubmitReply)}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Send Reply
                </>
              )}
            </Button>
          </div>
        }
      >
        {selectedInquiry && (
          <div className="space-y-6">
            {/* Inquiry Summary */}
            <div className="bg-muted/50 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{selectedInquiry.inquirerName}</span>
                <span className="text-muted-foreground">•</span>
                <span className="text-sm text-muted-foreground">{selectedInquiry.propertyTitle}</span>
              </div>
              <p className="text-sm text-muted-foreground line-clamp-2">
                {selectedInquiry.message}
              </p>
            </div>

            {/* Reply Form */}
            <Form {...replyForm}>
              <form className="space-y-4">
                <FormField
                  control={replyForm.control}
                  name="reply"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Your Reply</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Write your response to the inquirer..."
                          className="min-h-[200px] resize-none"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                      <p className="text-xs text-muted-foreground">
                        This reply will be sent to {selectedInquiry.inquirerEmail}
                      </p>
                    </FormItem>
                  )}
                />
              </form>
            </Form>
          </div>
        )}
      </AppDialog>
    </div>
  );
}
